export const getDocumentText = () => {
    return new Promise((resolve, reject) => {
        Word.run(async (context) => {
            const body = context.document.body;
            context.load(body, 'text');
            await context.sync();
            resolve(body.text);
        }).catch(reject);
    });
};
export const searchText = async (searchTerm) => {
    try {
        return await Word.run(async (context) => {
            const body = context.document.body;
            const trackedChanges = body.getTrackedChanges();
            context.load(trackedChanges, 'items');
            await context.sync();
            const searchResults = context.document.body.search(searchTerm, {
                matchCase: true,
                ignorePunct: true,
            });
            context.load(searchResults, 'items');
            await context.sync();
            // filter out tracked changes that have been marked as deleted
            const filteredResults = await Promise.all(searchResults.items.map(async (result) => {
                context.load(result, 'text');
                await context.sync();
                let isDeleted = false;
                for (const change of trackedChanges.items) {
                    const changeRange = change.getRange();
                    changeRange.load('text');
                    await context.sync();
                    if (changeRange.text.includes(result.text) &&
                        change.type === Word.TrackedChangeType.deleted) {
                        isDeleted = true;
                        break;
                    }
                }
                return isDeleted ? null : result;
            }));
            const validResults = filteredResults.filter((result) => result !== null);
            if (validResults.length > 0) {
                validResults[0].select();
                await context.sync();
            }
        });
    }
    catch (error) {
        console.error('Error: ' + error);
    }
};
// Compute the minimal text difference between original and suggested,
// returning only the changed portion to avoid replacing entire paragraphs
// when only a word or phrase has changed.
// Note: insertion-only changes (oldText is empty) are not handled here;
// callers fall back to full-range replacement in that case.
function computeMinimalReplacement(original, suggested) {
    if (original === suggested)
        return null;
    // Split into tokens preserving whitespace to find word-level boundaries
    const originalTokens = original.split(/(\s+)/);
    const suggestedTokens = suggested.split(/(\s+)/);
    // Find common prefix tokens
    let prefixCount = 0;
    while (prefixCount < originalTokens.length &&
        prefixCount < suggestedTokens.length &&
        originalTokens[prefixCount] === suggestedTokens[prefixCount]) {
        prefixCount++;
    }
    // Find common suffix tokens (from the remaining text after prefix)
    let suffixCount = 0;
    const maxSuffix = Math.min(originalTokens.length - prefixCount, suggestedTokens.length - prefixCount);
    while (suffixCount < maxSuffix &&
        originalTokens[originalTokens.length - 1 - suffixCount] ===
            suggestedTokens[suggestedTokens.length - 1 - suffixCount]) {
        suffixCount++;
    }
    const endOrig = suffixCount > 0 ? originalTokens.length - suffixCount : originalTokens.length;
    const endSugg = suffixCount > 0 ? suggestedTokens.length - suffixCount : suggestedTokens.length;
    const oldText = originalTokens.slice(prefixCount, endOrig).join('');
    const newText = suggestedTokens.slice(prefixCount, endSugg).join('');
    if (oldText === newText)
        return null;
    return { oldText, newText };
}
// Tolerance for gaps between consecutive chunk matches in the document text.
// Accounts for whitespace or formatting characters that Word may insert
// between chunks when text is split at arbitrary 255-character boundaries.
const CHUNK_GAP_TOLERANCE = 10;
// Find the range in the document matching the given text.
// Handles the Word API 255-character search limit by chunking long text.
async function findTextRange(context, body, fullDocumentText, documentText) {
    if (documentText.length <= 255) {
        const searchResults = body.search(documentText, {
            matchCase: false,
            matchWholeWord: false,
        });
        context.load(searchResults, 'items');
        await context.sync();
        if (searchResults.items.length > 0) {
            return searchResults.items[0];
        }
        return null;
    }
    const chunks = [];
    for (let i = 0; i < documentText.length; i += 255) {
        chunks.push(documentText.substring(i, i + 255));
    }
    let lastEndPosition = -1;
    const foundRanges = [];
    for (const chunk of chunks) {
        const searchResults = body.search(chunk, {
            matchCase: false,
            matchWholeWord: false,
        });
        context.load(searchResults, 'items');
        await context.sync();
        if (searchResults.items.length === 0) {
            break;
        }
        let chunkFound = false;
        for (const result of searchResults.items) {
            context.load(result, 'text');
            await context.sync();
            const resultPosition = fullDocumentText.indexOf(result.text, lastEndPosition + 1);
            if (resultPosition !== -1 &&
                (lastEndPosition === -1 || resultPosition <= lastEndPosition + CHUNK_GAP_TOLERANCE)) {
                lastEndPosition = resultPosition + result.text.length - 1;
                foundRanges.push(result);
                chunkFound = true;
                break;
            }
        }
        if (!chunkFound) {
            break;
        }
    }
    if (foundRanges.length === chunks.length) {
        return foundRanges[0].expandTo(foundRanges[foundRanges.length - 1]);
    }
    return null;
}
// Returns true if the text was replaced, false if the original text was not found.
export const replaceText = async (documentText, replaceWithText) => {
    try {
        return await Word.run(async (context) => {
            const body = context.document.body;
            context.load(body, 'text');
            await context.sync();
            const fullDocumentText = body.text;
            // Find the full range matching the original text
            const fullRange = await findTextRange(context, body, fullDocumentText, documentText);
            if (!fullRange)
                return false;
            // Compute minimal diff to replace only the changed portion.
            // Insertion-only changes (oldText is empty) fall through to
            // full-range replacement since we cannot pinpoint an insertion
            // position via search.
            const minimalDiff = computeMinimalReplacement(documentText, replaceWithText);
            if (minimalDiff &&
                minimalDiff.oldText.length > 0 &&
                minimalDiff.oldText.length <= 255) {
                // Verify the old text is unique within the original to avoid
                // replacing the wrong occurrence
                const firstIdx = documentText.indexOf(minimalDiff.oldText);
                const secondIdx = documentText.indexOf(minimalDiff.oldText, firstIdx + 1);
                const isUnique = firstIdx !== -1 && secondIdx === -1;
                if (isUnique) {
                    // Search within the found range for just the changed text
                    const subResults = fullRange.search(minimalDiff.oldText, {
                        matchCase: false,
                        matchWholeWord: false,
                    });
                    context.load(subResults, 'items');
                    await context.sync();
                    if (subResults.items.length > 0) {
                        subResults.items[0].select();
                        subResults.items[0].insertText(minimalDiff.newText, Word.InsertLocation.replace);
                        await context.sync();
                        return true;
                    }
                }
            }
            // Fallback: replace the full range (original behavior)
            fullRange.select();
            fullRange.insertText(replaceWithText, Word.InsertLocation.replace);
            await context.sync();
            return true;
        });
    }
    catch (error) {
        console.error('Error: ' + error);
        return false;
    }
};
export const isInDocument = async (searchText) => {
    try {
        return new Promise((resolve) => {
            Word.run(async (context) => {
                const body = context.document.body;
                context.load(body, 'text');
                await context.sync();
                const documentText = body.text;
                if (searchText.length <= 255) {
                    const searchResults = body.search(searchText, {
                        matchCase: false,
                        matchWholeWord: false,
                    });
                    context.load(searchResults, 'items');
                    await context.sync();
                    const trackedChanges = body.getTrackedChanges();
                    context.load(trackedChanges, 'items');
                    await context.sync();
                    // Use Promise.all + map (not filter) since filter does not await async callbacks
                    const filteredResults = await Promise.all(searchResults.items.map(async (result) => {
                        context.load(result, 'text');
                        await context.sync();
                        for (const change of trackedChanges.items) {
                            const changeRange = change.getRange();
                            changeRange.load('text');
                            await context.sync();
                            if (changeRange.text.includes(result.text) &&
                                change.type === Word.TrackedChangeType.deleted) {
                                return null;
                            }
                        }
                        return result;
                    }));
                    resolve(filteredResults.some((result) => result !== null));
                }
                else {
                    const range = await findTextRange(context, body, documentText, searchText);
                    resolve(range !== null);
                }
            });
        });
    }
    catch (error) {
        console.error('Error: ' + error);
        return false;
    }
};
